import { useEffect, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { AlertCircle, CheckCircle, Info, Copy, ExternalLink } from 'lucide-react';
import FacebookPageConnector, {
  FacebookPageSelection
} from '../../omnichannel/FacebookPageConnector';
import { httpClient } from '../../../services/httpClient';
import omnichannelService, { FacebookPage } from '../../../services/omnichannelService';

interface FormData {
  pageId: string;
  pageAccessToken: string;
  appSecret: string;
  verifyToken: string;
}

interface TestResult {
  success: boolean;
  message?: string;
  error?: string;
  pageInfo?: {
    id: string;
    name: string;
  };
}

export default function MessengerSetupForm() {
  const { theme } = useTheme();
  const { t } = useTranslation();

  const [formData, setFormData] = useState<FormData>({
    pageId: '',
    pageAccessToken: '',
    appSecret: '',
    verifyToken: 'my_secure_verify_token_' + Math.random().toString(36).substr(2, 9)
  });

  const [step, setStep] = useState(1);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testResult, setTestResult] = useState<TestResult | null>(null);
  const [copied, setCopied] = useState('');
  const [configSaved, setConfigSaved] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [facebookLoginStatus, setFacebookLoginStatus] = useState<
    'loading' | 'connected' | 'disconnected'
  >('loading');
  const [selectedFacebookPages, setSelectedFacebookPages] = useState<FacebookPageSelection[]>([]);
  const [availableFacebookPages, setAvailableFacebookPages] = useState<FacebookPage[]>([]);
  const [isRedirectingToFacebook, setIsRedirectingToFacebook] = useState(false);
  const [facebookStatusError, setFacebookStatusError] = useState<string | null>(null);
  const [isInstallingPages, setIsInstallingPages] = useState(false);
  const [installMessage, setInstallMessage] = useState<string | null>(null);
  const [installError, setInstallError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  useEffect(() => {
    checkFacebookLoginStatus();
  }, []);

  const checkFacebookLoginStatus = async () => {
    try {
      setFacebookStatusError(null);
      setFacebookLoginStatus('loading');
      const statusResult = await httpClient.get<{ success: boolean; logged_in: boolean }>(
        '/api/channels/messenger/login-status'
      );
      setFacebookLoginStatus(statusResult.logged_in ? 'connected' : 'disconnected');
    } catch (error: any) {
      console.error('Unable to read Facebook login status:', error);
      setFacebookStatusError('Unable to verify Facebook login. Please try again.');
      setFacebookLoginStatus('disconnected');
    }
  };

  const handleFacebookLogin = async () => {
    setIsRedirectingToFacebook(true);
    setFacebookStatusError(null);
    try {
      const loginResult = await httpClient.post<{ success: boolean; data: string }>(
        '/api/channels/messenger/request-login'
      );

      if (loginResult.success && loginResult.data) {
        window.location.href = loginResult.data;
        return;
      }

      throw new Error('Failed to initiate Facebook login');
    } catch (error: any) {
      console.error('Facebook login redirect error:', error);
      setFacebookStatusError(error.message || 'Unable to redirect to Facebook login');
      setIsRedirectingToFacebook(false);
    }
  };

  const updateSelection = (pages: FacebookPageSelection[]) => {
    setSelectedFacebookPages(pages);
    setInstallMessage(null);
    setInstallError(null);

    if (pages.length === 0) {
      setFormData((prev) => ({
        ...prev,
        pageId: '',
        pageAccessToken: ''
      }));
      return;
    }

    const active = pages[pages.length - 1];
    setFormData((prev) => ({
      ...prev,
      pageId: active.id,
      pageAccessToken: active.access_token
    }));
  };

  const handlePageSelection = (pages: FacebookPageSelection[]) => {
    updateSelection(pages);
  };

  const handlePagesLoaded = (pages: FacebookPage[]) => {
    setAvailableFacebookPages(pages);
    if (pages.length > 0 && selectedFacebookPages.length === 0) {
      updateSelection([
        {
          id: pages[0].id,
          access_token: pages[0].access_token,
          name: pages[0].name
        }
      ]);
    }
  };

  const handleInstallPages = async () => {
    if (selectedFacebookPages.length === 0) {
      setInstallError('Please select at least one page.');
      return;
    }

    setIsInstallingPages(true);
    setInstallMessage(null);
    setInstallError(null);
    try {
      await omnichannelService.subscribeFacebookPages(
        selectedFacebookPages.map((page) => ({
          page_id: page.id,
          access_token: page.access_token
        }))
      );
      setInstallMessage('Successfully enabled FlowCXP on the selected pages.');
    } catch (error: any) {
      console.error('Failed to install FlowCXP app on Facebook pages:', error);
      setInstallError(error.message || 'Failed to install FlowCXP on the selected pages.');
    } finally {
      setIsInstallingPages(false);
    }
  };

  const copyToClipboard = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(''), 2000);
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await httpClient.post<{
        success: boolean;
        pageInfo?: { id: string; name: string };
      }>('/api/channels/messenger/test', formData);
      const result = await response;
      setTestResult(result);

      if (result.success) {
        // Move to final step after successful test
        setTimeout(() => setStep(4), 1500);
      }
    } catch (error: any) {
      setTestResult({ success: false, error: error.message });
    }
    setTesting(false);
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const response = await httpClient.post<{
        success: boolean;
        error?: string;
        webhook_url: string;
      }>('/api/channels/messenger/config', formData);
      const result = await response;

      if (result.success) {
        setWebhookUrl(result.webhook_url);
        setConfigSaved(true);
        setStep(3); // Move to webhook setup step
      } else {
        alert('Error: ' + (result.error || 'Failed to save configuration'));
      }
    } catch (error: any) {
      alert('Error: ' + error.message);
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div
        className={`rounded-lg p-4 border ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}
      >
        {facebookLoginStatus === 'loading' && (
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>
            Checking Facebook login status…
          </p>
        )}

        {facebookLoginStatus === 'disconnected' && (
          <div className="space-y-3">
            <p>
              Connect your Facebook account to automatically load the pages you manage. We recommend
              you to include current and future pages when selecting the page scope in the Facebook
              login dialog to easily integrate new pages with our Atrium feature.
            </p>
            <button
              type="button"
              onClick={handleFacebookLogin}
              disabled={isRedirectingToFacebook}
              className={`w-full rounded-lg py-3 font-semibold transition ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-500 text-white'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              } ${isRedirectingToFacebook ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              {isRedirectingToFacebook ? 'Redirecting to Facebook…' : 'Login with Facebook'}
            </button>
            <p className="text-xs">You will be redirected to the Facebook Login page.</p>
            {facebookStatusError && <p className="text-sm text-red-500">{facebookStatusError}</p>}
          </div>
        )}
      </div>
    </div>
  );

  // return (
  //   <div className={`max-w-4xl mx-auto p-6 rounded-lg shadow-lg ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'
  //     }`}>
  //     <div className={`border-b pb-4 mb-6 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
  //       }`}>
  //       <h2 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
  //         }`}>
  //         Connect Facebook Messenger
  //       </h2>
  //       <p className={`mt-2 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
  //         }`}>
  //         Enable your customers to reach you through Facebook Messenger
  //       </p>
  //     </div>

  //     <div className="flex items-center justify-between mb-8">
  //       {[1, 2, 3, 4].map((s, index) => (
  //         <div key={s} className="flex items-center flex-1">
  //           <div className="relative">
  //             <div className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold z-10 relative ${step >= s
  //                 ? 'bg-indigo-600 text-white'
  //                 : theme === 'dark' ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
  //               }`}>
  //               {s}
  //             </div>
  //           </div>
  //           <div className="ml-3">
  //             <p className={`text-sm font-medium ${step >= s
  //                 ? 'text-indigo-600'
  //                 : theme === 'dark' ? 'text-gray-500' : 'text-gray-500'
  //               }`}>
  //               {s === 1 && 'Facebook Setup'}
  //               {s === 2 && 'Enter Credentials'}
  //               {s === 3 && 'Verify Webhook'}
  //               {s === 4 && 'Activate'}
  //             </p>
  //           </div>
  //         </div>
  //       ))}
  //     </div>

  //     {step === 1 && (
  //       <div className="space-y-6">
  //         <div className={`rounded-lg p-4 border ${theme === 'dark'
  //             ? 'bg-blue-900/20 border-blue-800'
  //             : 'bg-blue-50 border-blue-200'
  //           }`}>
  //           <div className="flex items-start">
  //             <Info className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
  //               }`} />
  //             <div>
  //               <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
  //                 }`}>Before you start</h3>
  //               <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
  //                 }`}>
  //                 You'll need a Facebook Page and a Facebook App with Messenger enabled.
  //                 Follow these steps to get your credentials.
  //               </p>
  //             </div>
  //           </div>
  //         </div>

  //         <div className="space-y-4">
  //           <h3 className={`font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'
  //             }`}>Step-by-step guide:</h3>

  //           <div className="space-y-3">
  //             {[
  //               {
  //                 num: 1,
  //                 title: 'Go to Facebook Developers',
  //                 desc: 'Visit developers.facebook.com and create a new app',
  //                 link: 'https://developers.facebook.com/apps/create/'
  //               },
  //               {
  //                 num: 2,
  //                 title: 'Add Messenger Product',
  //                 desc: 'In your app dashboard, add the Messenger product',
  //               },
  //               {
  //                 num: 3,
  //                 title: 'Connect Your Facebook Page',
  //                 desc: 'Select the page you want to receive messages from',
  //               },
  //               {
  //                 num: 4,
  //                 title: 'Generate Page Access Token',
  //                 desc: 'Click "Generate Token" and copy it (you\'ll need this in the next step)',
  //               },
  //               {
  //                 num: 5,
  //                 title: 'Get Your App Secret',
  //                 desc: 'Go to Settings → Basic and copy your App Secret',
  //               },
  //             ].map((item) => (
  //               <div key={item.num} className={`flex items-start p-3 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-50'
  //                 }`}>
  //                 <div className="w-7 h-7 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
  //                   {item.num}
  //                 </div>
  //                 <div className="ml-3 flex-1">
  //                   <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'
  //                     }`}>{item.title}</p>
  //                   <p className={`text-sm mt-0.5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
  //                     }`}>{item.desc}</p>
  //                   {item.link && (
  //                     <a
  //                       href={item.link}
  //                       target="_blank"
  //                       rel="noopener noreferrer"
  //                       className="text-sm text-indigo-600 hover:text-indigo-700 mt-1 inline-flex items-center"
  //                     >
  //                       Open in new tab <ExternalLink className="w-3 h-3 ml-1" />
  //                     </a>
  //                   )}
  //                 </div>
  //               </div>
  //             ))}
  //           </div>
  //         </div>

  //         <button
  //           onClick={() => setStep(2)}
  //           className="w-full bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
  //         >
  //           I have my credentials, continue →
  //         </button>
  //       </div>
  //     )}

  //     {step === 2 && (
  //       <div className="space-y-6">
  //         <div className={`rounded-lg p-4 border ${theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200'}`}>
  //           {facebookLoginStatus === 'loading' && (
  //             <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-500'}`}>Checking Facebook login status…</p>
  //           )}

  //           {facebookLoginStatus === 'disconnected' && (
  //             <div className="space-y-3">
  //               <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-600'}`}>
  //                 Connect your Facebook account to automatically load the pages you manage.
  //               </p>
  //               <button
  //                 type="button"
  //                 onClick={handleFacebookLogin}
  //                 disabled={isRedirectingToFacebook}
  //                 className={`w-full rounded-lg py-3 font-semibold transition ${theme === 'dark'
  //                   ? 'bg-blue-600 hover:bg-blue-500 text-white'
  //                   : 'bg-blue-500 hover:bg-blue-600 text-white'
  //                   } ${isRedirectingToFacebook ? 'opacity-60 cursor-not-allowed' : ''}`}
  //               >
  //                 {isRedirectingToFacebook ? 'Redirecting to Facebook…' : 'Login with Facebook'}
  //               </button>
  //               {facebookStatusError && (
  //                 <p className="text-sm text-red-500">{facebookStatusError}</p>
  //               )}
  //             </div>
  //           )}

  //           {facebookLoginStatus === 'connected' && (
  //             <div className="space-y-3">
  //               <FacebookPageConnector
  //                 initialSelection={selectedFacebookPages}
  //                 onSelectionChange={handlePageSelection}
  //                 onPagesLoaded={handlePagesLoaded}
  //               />
  //               <div className="space-y-2">
  //                 <button
  //                   type="button"
  //                   onClick={handleInstallPages}
  //                   disabled={isInstallingPages || selectedFacebookPages.length === 0}
  //                   className={`w-full rounded-lg py-3 font-semibold transition ${theme === 'dark'
  //                     ? 'bg-green-600 hover:bg-green-500 text-white'
  //                     : 'bg-green-500 hover:bg-green-600 text-white'
  //                     } ${isInstallingPages || selectedFacebookPages.length === 0 ? 'opacity-60 cursor-not-allowed' : ''}`}
  //                 >
  //                   {isInstallingPages ? 'Installing FlowCXP on selected pages…' : 'Install FlowCXP on selected pages'}
  //                 </button>
  //                 {installMessage && <p className="text-sm text-green-500">{installMessage}</p>}
  //                 {installError && <p className="text-sm text-red-500">{installError}</p>}
  //               </div>
  //             </div>
  //           )}
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             Facebook Page ID
  //           </label>
  //           <input
  //             type="text"
  //             name="pageId"
  //             value={formData.pageId}
  //             onChange={handleChange}
  //             placeholder="123456789012345"
  //             className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${theme === 'dark'
  //                 ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
  //                 : 'border-gray-300 placeholder-gray-400'
  //               }`}
  //           />
  //           <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
  //             }`}>
  //             Find this in your Facebook Page Settings → About → Page ID
  //           </p>
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             Page Access Token
  //           </label>
  //           <textarea
  //             name="pageAccessToken"
  //             value={formData.pageAccessToken}
  //             onChange={handleChange}
  //             placeholder="EAAxxxxxxxxxxxxx..."
  //             rows={3}
  //             className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono text-sm ${theme === 'dark'
  //                 ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
  //                 : 'border-gray-300 placeholder-gray-400'
  //               }`}
  //           />
  //           <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
  //             }`}>
  //             Generated from Messenger Settings → Access Tokens in your Facebook App
  //           </p>
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             App Secret
  //           </label>
  //           <input
  //             type="password"
  //             name="appSecret"
  //             value={formData.appSecret}
  //             onChange={handleChange}
  //             placeholder="abcdef1234567890"
  //             className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono ${theme === 'dark'
  //                 ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
  //                 : 'border-gray-300 placeholder-gray-400'
  //               }`}
  //           />
  //           <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
  //             }`}>
  //             Found in Settings → Basic in your Facebook App
  //           </p>
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             Verify Token
  //           </label>
  //           <div className="flex gap-2">
  //             <input
  //               type="text"
  //               name="verifyToken"
  //               value={formData.verifyToken}
  //               onChange={handleChange}
  //               className={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent font-mono ${theme === 'dark'
  //                   ? 'bg-gray-700 border-gray-600 text-white'
  //                   : 'border-gray-300'
  //                 }`}
  //             />
  //             <button
  //               type="button"
  //               onClick={() => copyToClipboard(formData.verifyToken, 'verify')}
  //               className={`px-4 py-2 border rounded-lg transition-colors ${theme === 'dark'
  //                   ? 'border-gray-600 hover:bg-gray-700'
  //                   : 'border-gray-300 hover:bg-gray-50'
  //                 }`}
  //             >
  //               {copied === 'verify' ? (
  //                 <CheckCircle className="w-5 h-5 text-green-600" />
  //               ) : (
  //                 <Copy className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
  //               )}
  //             </button>
  //           </div>
  //           <p className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
  //             }`}>
  //             Custom token for webhook verification (randomly generated)
  //           </p>
  //         </div>

  //         <div className="flex gap-3">
  //           <button
  //             type="button"
  //             onClick={() => setStep(1)}
  //             className={`flex-1 border py-3 rounded-lg font-semibold transition-colors ${theme === 'dark'
  //                 ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
  //                 : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  //               }`}
  //           >
  //             ← Back
  //           </button>
  //           <button
  //             type="button"
  //             onClick={handleSaveConfig}
  //             disabled={saving || !formData.pageId || !formData.pageAccessToken || !formData.appSecret}
  //             className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
  //           >
  //             {saving ? 'Saving...' : 'Save Configuration →'}
  //           </button>
  //         </div>
  //       </div>
  //     )}

  //     {step === 3 && (
  //       <div className="space-y-6">
  //         <div className={`rounded-lg p-4 border ${theme === 'dark'
  //             ? 'bg-green-900/20 border-green-800'
  //             : 'bg-green-50 border-green-200'
  //           }`}>
  //           <div className="flex items-start">
  //             <CheckCircle className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'
  //               }`} />
  //             <div>
  //               <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-green-300' : 'text-green-900'
  //                 }`}>Configuration Saved!</h3>
  //               <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'
  //                 }`}>
  //                 Now configure the webhook in your Facebook App to verify the connection.
  //               </p>
  //             </div>
  //           </div>
  //         </div>

  //         <div className={`rounded-lg p-4 border ${theme === 'dark'
  //             ? 'bg-yellow-900/20 border-yellow-800'
  //             : 'bg-yellow-50 border-yellow-200'
  //           }`}>
  //           <div className="flex items-start">
  //             <AlertCircle className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${theme === 'dark' ? 'text-yellow-400' : 'text-yellow-600'
  //               }`} />
  //             <div>
  //               <h3 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-yellow-300' : 'text-yellow-900'
  //                 }`}>Setup Facebook Webhook</h3>
  //               <p className={`text-sm mb-3 ${theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
  //                 }`}>
  //                 Follow these steps in your Facebook App:
  //               </p>
  //               <ol className={`text-sm space-y-1 list-decimal list-inside ${theme === 'dark' ? 'text-yellow-200' : 'text-yellow-800'
  //                 }`}>
  //                 <li>Go to Messenger Settings in your Facebook App</li>
  //                 <li>Find "Webhooks" section</li>
  //                 <li>Click "Add Callback URL"</li>
  //                 <li>Paste the URL and verify token below</li>
  //                 <li>Click "Verify and Save"</li>
  //                 <li>Subscribe to webhook fields (see below)</li>
  //               </ol>
  //             </div>
  //           </div>
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             Webhook Callback URL
  //           </label>
  //           <div className="flex gap-2">
  //             <input
  //               type="text"
  //               value={webhookUrl}
  //               readOnly
  //               className={`flex-1 px-4 py-2 border rounded-lg font-mono text-sm ${theme === 'dark'
  //                   ? 'bg-gray-700 border-gray-600 text-white'
  //                   : 'bg-gray-50 border-gray-300'
  //                 }`}
  //             />
  //             <button
  //               type="button"
  //               onClick={() => copyToClipboard(webhookUrl, 'webhook')}
  //               className={`px-4 py-2 border rounded-lg transition-colors ${theme === 'dark'
  //                   ? 'border-gray-600 hover:bg-gray-700'
  //                   : 'border-gray-300 hover:bg-gray-50'
  //                 }`}
  //             >
  //               {copied === 'webhook' ? (
  //                 <CheckCircle className="w-5 h-5 text-green-600" />
  //               ) : (
  //                 <Copy className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
  //               )}
  //             </button>
  //           </div>
  //         </div>

  //         <div>
  //           <label className={`block text-sm font-medium mb-2 ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //             }`}>
  //             Verify Token (use this in Facebook)
  //           </label>
  //           <div className="flex gap-2">
  //             <input
  //               type="text"
  //               value={formData.verifyToken}
  //               readOnly
  //               className={`flex-1 px-4 py-2 border rounded-lg font-mono text-sm ${theme === 'dark'
  //                   ? 'bg-gray-700 border-gray-600 text-white'
  //                   : 'bg-gray-50 border-gray-300'
  //                 }`}
  //             />
  //             <button
  //               type="button"
  //               onClick={() => copyToClipboard(formData.verifyToken, 'verify2')}
  //               className={`px-4 py-2 border rounded-lg transition-colors ${theme === 'dark'
  //                   ? 'border-gray-600 hover:bg-gray-700'
  //                   : 'border-gray-300 hover:bg-gray-50'
  //                 }`}
  //             >
  //               {copied === 'verify2' ? (
  //                 <CheckCircle className="w-5 h-5 text-green-600" />
  //               ) : (
  //                 <Copy className={`w-5 h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
  //               )}
  //             </button>
  //           </div>
  //         </div>

  //         <div className={`rounded-lg p-4 border ${theme === 'dark'
  //             ? 'bg-blue-900/20 border-blue-800'
  //             : 'bg-blue-50 border-blue-200'
  //           }`}>
  //           <p className={`text-sm ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
  //             }`}>
  //             <strong>Subscribe to these webhook fields:</strong> messages, messaging_postbacks, message_reads, message_deliveries
  //           </p>
  //         </div>

  //         <div className={`border-t pt-6 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'
  //           }`}>
  //           <h3 className={`font-semibold mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
  //             }`}>After Webhook is Verified</h3>
  //           <p className={`text-sm mb-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
  //             }`}>
  //             Once Facebook successfully verifies your webhook, click the button below to test the connection and activate the channel.
  //           </p>

  //           <button
  //             type="button"
  //             onClick={testConnection}
  //             disabled={testing}
  //             className="w-full bg-green-600 text-white py-3 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400"
  //           >
  //             {testing ? 'Testing Connection...' : 'Test Connection & Activate'}
  //           </button>

  //           {testResult && (
  //             <div className={`mt-4 p-4 rounded-lg border ${testResult.success
  //                 ? theme === 'dark'
  //                   ? 'bg-green-900/20 border-green-800'
  //                   : 'bg-green-50 border-green-200'
  //                 : theme === 'dark'
  //                   ? 'bg-red-900/20 border-red-800'
  //                   : 'bg-red-50 border-red-200'
  //               }`}>
  //               <div className="flex items-start">
  //                 {testResult.success ? (
  //                   <CheckCircle className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'
  //                     }`} />
  //                 ) : (
  //                   <AlertCircle className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${theme === 'dark' ? 'text-red-400' : 'text-red-600'
  //                     }`} />
  //                 )}
  //                 <div>
  //                   <p className={`font-semibold ${testResult.success
  //                       ? theme === 'dark' ? 'text-green-300' : 'text-green-900'
  //                       : theme === 'dark' ? 'text-red-300' : 'text-red-900'
  //                     }`}>
  //                     {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
  //                   </p>
  //                   <p className={`text-sm mt-1 ${testResult.success
  //                       ? theme === 'dark' ? 'text-green-200' : 'text-green-800'
  //                       : theme === 'dark' ? 'text-red-200' : 'text-red-800'
  //                     }`}>
  //                     {testResult.message || testResult.error}
  //                   </p>
  //                 </div>
  //               </div>
  //             </div>
  //           )}
  //         </div>

  //         <div className="flex gap-3 mt-6">
  //           <button
  //             type="button"
  //             onClick={() => setStep(2)}
  //             className={`flex-1 border py-3 rounded-lg font-semibold transition-colors ${theme === 'dark'
  //                 ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
  //                 : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  //               }`}
  //           >
  //             ← Back to Edit
  //           </button>
  //         </div>
  //       </div>
  //     )}

  //     {step === 4 && (
  //       <div className="space-y-6">
  //         <div className={`rounded-lg p-6 border-2 ${theme === 'dark'
  //             ? 'bg-green-900/30 border-green-700'
  //             : 'bg-green-50 border-green-300'
  //           }`}>
  //           <div className="flex items-start">
  //             <CheckCircle className={`w-8 h-8 mt-0.5 mr-4 flex-shrink-0 ${theme === 'dark' ? 'text-green-400' : 'text-green-600'
  //               }`} />
  //             <div>
  //               <h3 className={`text-xl font-bold mb-2 ${theme === 'dark' ? 'text-green-300' : 'text-green-900'
  //                 }`}>🎉 Facebook Messenger Connected!</h3>
  //               <p className={`text-sm ${theme === 'dark' ? 'text-green-200' : 'text-green-800'
  //                 }`}>
  //                 Your Facebook Messenger channel is now active and ready to receive messages.
  //               </p>
  //             </div>
  //           </div>
  //         </div>

  //         {testResult?.pageInfo && (
  //           <div className={`rounded-lg p-4 border ${theme === 'dark'
  //               ? 'bg-gray-700 border-gray-600'
  //               : 'bg-gray-50 border-gray-200'
  //             }`}>
  //             <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'
  //               }`}>Connected Page</h4>
  //             <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //               }`}>
  //               <strong>Page Name:</strong> {testResult.pageInfo.name}
  //             </p>
  //             <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
  //               }`}>
  //               <strong>Page ID:</strong> {testResult.pageInfo.id}
  //             </p>
  //           </div>
  //         )}

  //         <div className={`rounded-lg p-4 border ${theme === 'dark'
  //             ? 'bg-blue-900/20 border-blue-800'
  //             : 'bg-blue-50 border-blue-200'
  //           }`}>
  //           <h4 className={`font-semibold mb-2 ${theme === 'dark' ? 'text-blue-300' : 'text-blue-900'
  //             }`}>What's Next?</h4>
  //           <ul className={`text-sm space-y-1 list-disc list-inside ${theme === 'dark' ? 'text-blue-200' : 'text-blue-800'
  //             }`}>
  //             <li>Send a test message to your Facebook Page</li>
  //             <li>Check the Conversations tab to see incoming messages</li>
  //             <li>Assign conversations to agents</li>
  //             <li>Reply to customers directly from the system</li>
  //           </ul>
  //         </div>

  //         <div className="flex gap-3">
  //           <button
  //             type="button"
  //             onClick={() => window.location.reload()}
  //             className={`flex-1 border py-3 rounded-lg font-semibold transition-colors ${theme === 'dark'
  //                 ? 'border-gray-600 text-gray-300 hover:bg-gray-700'
  //                 : 'border-gray-300 text-gray-700 hover:bg-gray-50'
  //               }`}
  //           >
  //             Configure Another Channel
  //           </button>
  //           <button
  //             type="button"
  //             onClick={() => window.location.href = '/conversations'}
  //             className="flex-1 bg-indigo-600 text-white py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
  //           >
  //             Go to Conversations →
  //           </button>
  //         </div>
  //       </div>
  //     )}
  //   </div>
  // );
}
